/**
 * useRotoTube.js — TanStack Query hooks for the RotoTube media library.
 *
 * Reads the three read-only media sources (Vimeo, HDPhotoHub, Frame.io) exposed
 * by the backend at /api/media/*. No mutations except the Frame.io disconnect.
 * Playback URLs are hit directly by <video>/<img>/<iframe>, not through here.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { logAction } from '../lib/actionLogger';

const STALE = 5 * 60 * 1000; // media listings change slowly — 5 min

export function useMediaSources() {
  return useQuery({
    queryKey: ['media', 'sources'],
    queryFn: () => api.get('/media/sources'),
    staleTime: STALE,
  });
}

export function useVimeoVideos(enabled = true) {
  return useQuery({
    queryKey: ['media', 'vimeo'],
    queryFn: () => api.get('/media/vimeo'),
    staleTime: STALE,
    enabled,
  });
}

export function useHdphSites(enabled = true) {
  return useQuery({
    queryKey: ['media', 'hdphotohub'],
    queryFn: () => api.get('/media/hdphotohub/sites'),
    staleTime: STALE,
    enabled,
  });
}

// Re-reads one shoot after its media has been woken in HDPhotoHub, without
// re-pulling the whole delivery catalog.
export function useRefreshShoot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sid) => api.post(`/media/hdphotohub/site/${sid}/refresh`),
    onSuccess: (site) => {
      qc.setQueryData(['media', 'hdphotohub'], (prev) => (
        Array.isArray(prev) && site ? prev.map((s) => (s.sid === site.sid ? site : s)) : prev
      ));
    },
  });
}

export function useFrameioProjects(enabled = true) {
  return useQuery({
    queryKey: ['media', 'frameio', 'projects'],
    queryFn: () => api.get('/media/frameio/projects'),
    staleTime: STALE,
    enabled,
    retry: false, // a 409 reconnect_required shouldn't retry
  });
}

export function useFrameioFolder(folderId, enabled = true) {
  return useQuery({
    queryKey: ['media', 'frameio', 'folder', folderId],
    queryFn: () => api.get(`/media/frameio/folder/${folderId}?videoOnly=1`),
    staleTime: STALE,
    enabled: enabled && !!folderId,
    retry: false,
  });
}

export function useDisconnectFrameio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/media/frameio/disconnect'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['media', 'sources'] });
      qc.invalidateQueries({ queryKey: ['media', 'frameio'] });
      logAction({
        action: 'Disconnect Frame.io',
        description: 'Disconnected the Frame.io media integration',
      });
    },
  });
}

// ── Viewer tools: descriptors + play/download helpers ──────────
// A "descriptor" is the payload sent to the backend to materialize a media_items
// row on first favorite / add-to-collection. Built from whatever a card already
// renders, so acting on an item needs no extra fetch.
export function toDescriptor(source, raw) {
  if (source === 'vimeo') {
    return {
      source, sourceId: String(raw.id), kind: 'video-url', title: raw.name,
      thumb: raw.thumbnail || null, duration: raw.duration || null,
      playRef: { src: `/api/media/vimeo/${raw.id}/play` },
    };
  }
  if (source === 'frameio') {
    return {
      source, sourceId: String(raw.id), kind: 'video-url', title: raw.name,
      thumb: raw.thumbnail || null,
      playRef: { src: `/api/media/frameio/file/${raw.id}/play` },
    };
  }
  if (source === 'hdphotohub') {
    if (raw.type === 'image') {
      return {
        source, sourceId: String(raw.mid), kind: 'image', title: raw.name,
        thumb: `/api/media/hdphotohub/thumb/${raw.mid}`,
        playRef: { url: raw.url },
      };
    }
    return {
      source, sourceId: String(raw.mid), kind: 'stream-iframe', title: raw.name,
      thumb: raw.thumb || null,
      playRef: { streamUid: raw.streamUid || null, embedurl: raw.embedurl || null },
    };
  }
  return null;
}

// A stable identity key for matching a card against the favorites set.
export const mediaKey = (source, sourceId) => `${source}:${sourceId}`;

// Turn a stored media_items row into the { kind, src, title } shape PlayerModal wants.
// Stored HDPhotoHub delivery URLs can rotate, so stills play through the authed
// re-resolve endpoint instead of the raw stored URL. Vimeo/Frame.io playRef.src
// already points at stable backend proxies; Stream embeds are stable public URLs.
export function itemToPlay(item) {
  if (item.kind === 'image') return { kind: 'image', src: `/api/media/item/${item.id}/play`, title: item.title };
  if (item.kind === 'stream-iframe') {
    const src = item.playRef?.streamUid
      ? `https://iframe.videodelivery.net/${item.playRef.streamUid}`
      : item.playRef?.embedurl;
    return { kind: 'stream-iframe', src, title: item.title };
  }
  return { kind: 'video-url', src: item.playRef?.src, title: item.title };
}

// Display thumb for a stored item. Vimeo CDN thumbs are long-lived; everything
// else (signed Frame.io thumbs, rotating HDPhotoHub URLs) re-resolves fresh.
export const itemThumb = (item) => (item.source === 'vimeo' && item.thumb) ? item.thumb : `/api/media/item/${item.id}/thumb`;

// HDPhotoHub Stream videos have no downloadable file; everything else does.
export const canDownload = (x) => !(x.source === 'hdphotohub' && x.kind === 'stream-iframe');
export const downloadUrl = (source, sourceId) => `/api/media/download/${source}/${sourceId}`;

// ── Favorites (team-shared) ────────────────────────────────────
export function useFavorites() {
  return useQuery({
    queryKey: ['media', 'favorites'],
    queryFn: () => api.get('/media/favorites'),
    staleTime: STALE,
  });
}
export function useAddFavorite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (descriptor) => api.post('/media/favorites', descriptor),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['media', 'favorites'] }),
  });
}
export function useRemoveFavorite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId) => api.del(`/media/favorites/${itemId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['media', 'favorites'] }),
  });
}

// ── Collections (team-shared) ──────────────────────────────────
export function useCollections() {
  return useQuery({
    queryKey: ['media', 'collections'],
    queryFn: () => api.get('/media/collections'),
    staleTime: STALE,
  });
}
export function useCollection(id) {
  return useQuery({
    queryKey: ['media', 'collection', id],
    queryFn: () => api.get(`/media/collections/${id}`),
    enabled: !!id,
    staleTime: STALE,
  });
}
function invalidateCollections(qc, id) {
  qc.invalidateQueries({ queryKey: ['media', 'collections'] });
  if (id) qc.invalidateQueries({ queryKey: ['media', 'collection', id] });
}
export function useCreateCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/media/collections', body),
    onSuccess: (data) => {
      invalidateCollections(qc);
      logAction({
        action: 'Create Media Collection',
        description: `Created media collection "${data.name}"`,
        details: { id: data.id, name: data.name },
      });
    },
  });
}
export function useUpdateCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) => api.put(`/media/collections/${id}`, body),
    onSuccess: (_d, v) => {
      invalidateCollections(qc, v.id);
      logAction({
        action: 'Update Media Collection',
        description: `Updated media collection "${_d.name}"`,
        details: { id: _d.id, name: _d.name },
      });
    },
  });
}
export function useDeleteCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.del(`/media/collections/${id}`),
    onSuccess: (_d, id) => {
      invalidateCollections(qc);
      logAction({
        action: 'Delete Media Collection',
        description: 'Deleted media collection',
        details: { id },
      });
    },
  });
}
export function useAddToCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ collectionId, descriptor }) => api.post(`/media/collections/${collectionId}/items`, descriptor),
    onSuccess: (_d, v) => invalidateCollections(qc, v.collectionId),
  });
}
export function useRemoveFromCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ collectionId, itemId }) => api.del(`/media/collections/${collectionId}/items/${itemId}`),
    onSuccess: (_d, v) => invalidateCollections(qc, v.collectionId),
  });
}

// ── Shares (client delivery links) ─────────────────────────────
export function useShares() {
  return useQuery({
    queryKey: ['media', 'shares'],
    queryFn: () => api.get('/media/shares'),
    staleTime: STALE,
  });
}
export function useCreateShare() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/media/shares', body),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['media', 'shares'] });
      logAction({
        action: 'Create Share Link',
        description: `Created client share link "${data.title}" (${data.itemCount} items) for ${data.clientName}`,
        details: {
          id: data.id, title: data.title, clientName: data.clientName,
          itemCount: data.itemCount, allowDownload: data.allowDownload,
          expiresAt: data.expiresAt, path: data.path,
        },
      });
    },
  });
}
export function useUpdateShare() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) => api.put(`/media/shares/${id}`, body),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['media', 'shares'] });
      logAction({
        action: 'Revoke Share Link',
        description: `Revoked client share link "${data.title}"`,
        details: { id: data.id, title: data.title, status: data.status },
      });
    },
  });
}
export function useDeleteShare() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.del(`/media/shares/${id}`),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ['media', 'shares'] });
      logAction({
        action: 'Delete Share Link',
        description: 'Deleted client share link',
        details: { id },
      });
    },
  });
}
